/**
 * /admin/memberships — aprobaciones manuales de membresía.
 *
 * Flujo: usuario paga → pending → admin pega URL de acceso FREE
 * (Telegram privado u otra URL free, no checkout) + mensaje opcional →
 * aprueba y envía email al comprador con ese destino.
 */

import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { useToast } from "@/hooks/use-toast";
import { useAdminAuth } from "@/hooks/useAdminAuth";
import { AdminPageHeader } from "@/components/admin/AdminPageHeader";
import {
  CheckCircle2,
  Clock,
  Crown,
  ExternalLink,
  Loader2,
  Mail,
  RefreshCw,
  Save,
  Sparkles,
  User,
} from "lucide-react";
import { useCallback, useEffect, useState } from "react";
import { Link } from "react-router-dom";

interface Membership {
  id: string;
  email: string;
  name: string | null;
  level_code: string;
  status: string;
  payment_provider: string;
  payment_id: string | null;
  amount: number | null;
  currency: string | null;
  country: string | null;
  started_at: string | null;
  approved: boolean | null;
  approved_at: string | null;
  approved_by: string | null;
  telegram_invite_url: string | null;
  pending_notes: string | null;
}

type RowState = {
  accessUrl: string;
  personalMessage: string;
  notes: string;
  saving: boolean;
};

function formatDate(iso: string | null): string {
  if (!iso) return "—";
  try {
    return new Date(iso).toLocaleString("es-AR", {
      day: "2-digit",
      month: "short",
      year: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    });
  } catch {
    return iso;
  }
}

function formatAmount(cents: number | null, currency: string | null): string {
  if (cents == null) return "—";
  const amount = cents / 100;
  const cur = (currency ?? "USD").toUpperCase();
  return new Intl.NumberFormat("es-AR", {
    style: "currency",
    currency: cur,
    maximumFractionDigits: 2,
  }).format(amount);
}

function looksLikePaidUrl(url: string): boolean {
  try {
    const p = new URL(url).pathname.toLowerCase();
    return (
      p.includes("checkout") ||
      p.includes("invitacion-exclusive") ||
      p.includes("invitacion-exclusiva") ||
      p.includes("create-subscription")
    );
  } catch {
    return false;
  }
}

export default function MembershipApprovals() {
  const { toast } = useToast();
  const { getAuthHeaders, isLoaded } = useAdminAuth();

  const [memberships, setMemberships] = useState<Membership[]>([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState<"awaiting" | "approved" | "all">("awaiting");
  const [stats, setStats] = useState<{
    awaiting_admin?: number;
    approved?: number;
    pending_approval?: number;
    total?: number;
  }>({});

  const [rowState, setRowState] = useState<Record<string, RowState>>({});

  const load = useCallback(async () => {
    if (!isLoaded) return;
    setLoading(true);
    try {
      const headers = await getAuthHeaders();
      const res = await fetch("/api/admin/memberships", { headers });
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      const data: { memberships: Membership[]; stats: typeof stats } = await res.json();
      setMemberships(data.memberships ?? []);
      setStats(data.stats ?? {});

      const initial: Record<string, RowState> = {};
      for (const m of data.memberships ?? []) {
        initial[m.id] = {
          accessUrl: m.telegram_invite_url ?? "",
          personalMessage: "",
          notes: m.pending_notes ?? "",
          saving: false,
        };
      }
      setRowState(initial);
    } catch (err) {
      toast({
        title: "Error al cargar",
        description: err instanceof Error ? err.message : "Error desconocido",
        variant: "destructive",
      });
    } finally {
      setLoading(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [getAuthHeaders, isLoaded]);

  useEffect(() => {
    void load();
  }, [load]);

  const approve = async (m: Membership) => {
    const state = rowState[m.id] ?? {
      accessUrl: "",
      personalMessage: "",
      notes: "",
      saving: false,
    };
    const accessUrl = state.accessUrl.trim();
    if (!accessUrl || !accessUrl.startsWith("https://")) {
      toast({
        title: "Falta URL de acceso free",
        description:
          "Pegá la URL free/privada (Telegram u otra https). No uses el link de pago.",
        variant: "destructive",
      });
      return;
    }
    if (looksLikePaidUrl(accessUrl)) {
      toast({
        title: "Esa parece URL de pago",
        description:
          "El destino debe ser free (Telegram, canal free, otra URL). No checkout ni /invitacion-exclusive.",
        variant: "destructive",
      });
      return;
    }

    setRowState((prev) => ({ ...prev, [m.id]: { ...state, saving: true } }));
    try {
      const headers = await getAuthHeaders();
      const res = await fetch("/api/admin/memberships/approve", {
        method: "POST",
        headers: { ...headers, "Content-Type": "application/json" },
        body: JSON.stringify({
          email: m.email,
          access_url: accessUrl,
          personal_message: state.personalMessage.trim() || undefined,
          notes: state.notes.trim() || undefined,
          send_email: true,
        }),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.error || `HTTP ${res.status}`);
      toast({
        title: data.email_sent ? "✓ Aprobado y email enviado" : "✓ Aprobado",
        description: data.email_sent
          ? `${m.email} — mail con URL free`
          : `${m.email} — guardado${data.email_error ? ` (mail: ${data.email_error})` : ""}`,
      });
      void load();
    } catch (err) {
      toast({
        title: "Error al aprobar",
        description: err instanceof Error ? err.message : "Error desconocido",
        variant: "destructive",
      });
    } finally {
      setRowState((prev) => ({
        ...prev,
        [m.id]: { ...prev[m.id], saving: false },
      }));
    }
  };

  const filtered = memberships.filter((m) => {
    if (filter === "awaiting") {
      // approved may be false OR null — both need admin action
      return (
        m.status === "pending_approval" ||
        (m.status === "active" && m.approved !== true)
      );
    }
    if (filter === "approved") return m.approved === true;
    return true;
  });

  return (
    <div className="admin-page mx-auto w-full max-w-6xl space-y-6">
      <AdminPageHeader
        description={
          <span className="inline-flex items-start gap-2">
            <Crown className="mt-0.5 h-5 w-5 shrink-0 text-amber-400" aria-hidden />
            <span>
              Flujo: <strong>paga → perfil → In review → aprobás</strong> y enviás email con la{" "}
              <strong>URL free</strong> (otra URL, no la de pago). El usuario ve el paso «In
              review» en /pending-approval. Segmento →{" "}
              <Link to="/admin/buyers" className="underline underline-offset-2">
                Compradores
              </Link>
              .
            </span>
          </span>
        }
        actions={
          <Button onClick={() => void load()} disabled={loading} variant="outline" className="gap-2">
            {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : <RefreshCw className="h-4 w-4" />}
            Refrescar
          </Button>
        }
      />

      <div className="rounded-xl border border-border bg-muted/30 px-4 py-3 text-sm text-muted-foreground leading-relaxed">
        <strong className="text-foreground">URL free ≠ link de compra.</strong> Acá pegás el
        destino de acceso (Telegram privado, canal free, landing free, etc.). El mail sale de{" "}
        <code className="text-xs">mail.anaafernandez.com</code> al email del comprador con ese
        botón.
      </div>

      {/* Stats */}
      <div className="flex flex-wrap items-center gap-2 text-xs">
        <span className="text-muted-foreground font-medium uppercase tracking-[0.18em] mr-1">
          Resumen:
        </span>
        <Badge className="gap-1.5 bg-amber-500/20 text-amber-200 border-amber-500/30 hover:bg-amber-500/20">
          <Clock className="h-3 w-3" />
          <span className="font-semibold tabular-nums">{stats.awaiting_admin ?? 0}</span> in review
        </Badge>
        <Badge className="gap-1.5 bg-emerald-500/20 text-emerald-200 border-emerald-500/30 hover:bg-emerald-500/20">
          <CheckCircle2 className="h-3 w-3" />
          <span className="font-semibold tabular-nums">{stats.approved ?? 0}</span> aprobados
        </Badge>
        <Badge variant="secondary" className="gap-1.5 tabular-nums">
          <span className="font-semibold">{stats.total ?? 0}</span> total
        </Badge>
      </div>

      {/* Filters */}
      <div className="flex flex-wrap gap-2">
        {(["awaiting", "approved", "all"] as const).map((f) => (
          <Button
            key={f}
            variant="outline"
            size="sm"
            onClick={() => setFilter(f)}
            className={
              filter === f
                ? "bg-muted border-border text-foreground"
                : "bg-transparent border-border text-muted-foreground hover:bg-muted/40 hover:text-foreground"
            }
          >
            {f === "awaiting" && "🟠 In review"}
            {f === "approved" && "✅ Aprobados"}
            {f === "all" && "Todos"}
          </Button>
        ))}
      </div>

      {loading ? (
        <div className="flex justify-center py-12">
          <Loader2 className="w-8 h-8 animate-spin text-muted-foreground" />
        </div>
      ) : filtered.length === 0 ? (
        <div className="rounded-2xl border border-dashed border-border bg-muted/30 py-12 text-center text-sm text-muted-foreground">
          {filter === "awaiting"
            ? "Nadie en In review — sin pendientes de aprobación."
            : filter === "approved"
              ? "No hay users aprobados todavía."
              : "Sin memberships."}
        </div>
      ) : (
        <div className="space-y-3">
          {filtered.map((m) => {
            const state = rowState[m.id] ?? {
              accessUrl: "",
              personalMessage: "",
              notes: "",
              saving: false,
            };
            const isApproved = m.approved === true;
            const levelColor =
              m.level_code === "vip"
                ? "bg-amber-500/20 text-amber-200 border-amber-500/30"
                : m.level_code === "platinum"
                  ? "bg-purple-500/20 text-purple-200 border-purple-500/30"
                  : "bg-secondary text-secondary-foreground border-border";
            const canSubmit =
              state.accessUrl.trim().startsWith("https://") &&
              !looksLikePaidUrl(state.accessUrl.trim());

            return (
              <div
                key={m.id}
                className={`rounded-2xl border bg-muted/40 p-5 space-y-4 ${
                  isApproved ? "border-emerald-500/30" : "border-border"
                }`}
              >
                <div className="grid sm:grid-cols-2 gap-4 text-sm">
                  <div className="space-y-2">
                    <div className="flex items-center gap-2">
                      <Mail className="w-4 h-4 text-muted-foreground shrink-0" />
                      <code className="text-foreground font-mono text-sm break-all">{m.email}</code>
                    </div>
                    {m.name && (
                      <div className="flex items-center gap-2">
                        <User className="w-4 h-4 text-muted-foreground shrink-0" />
                        <span className="text-muted-foreground">{m.name}</span>
                      </div>
                    )}
                    <div className="flex items-center gap-2 flex-wrap">
                      <Badge className={`gap-1 ${levelColor} hover:opacity-100`}>
                        <Crown className="h-3 w-3" />
                        {m.level_code.toUpperCase()}
                      </Badge>
                      {m.country && (
                        <Badge variant="outline" className="font-mono text-[11px]">
                          {m.country}
                        </Badge>
                      )}
                      {isApproved ? (
                        <Badge className="gap-1 bg-emerald-500/20 text-emerald-200 border-emerald-500/30 hover:bg-emerald-500/20">
                          <CheckCircle2 className="h-3 w-3" />
                          Aprobado
                        </Badge>
                      ) : (
                        <Badge className="gap-1 bg-amber-500/20 text-amber-200 border-amber-500/30 hover:bg-amber-500/20">
                          <Clock className="h-3 w-3" />
                          In review
                        </Badge>
                      )}
                    </div>
                  </div>

                  <div className="space-y-1 text-xs text-muted-foreground">
                    <div>
                      <span>Pago:</span>{" "}
                      <span className="text-foreground font-mono tabular-nums">
                        {formatAmount(m.amount, m.currency)}
                      </span>{" "}
                      · <span className="uppercase">{m.payment_provider}</span>
                    </div>
                    {m.payment_id && (
                      <div>
                        Payment ID:{" "}
                        <code className="font-mono text-[10px] break-all">{m.payment_id}</code>
                      </div>
                    )}
                    <div>
                      Comprado:{" "}
                      <span className="tabular-nums">{formatDate(m.started_at)}</span>
                    </div>
                    {isApproved && m.approved_at && (
                      <div>
                        Aprobado:{" "}
                        <span className="text-emerald-300 tabular-nums">
                          {formatDate(m.approved_at)}
                        </span>
                      </div>
                    )}
                    {m.telegram_invite_url && (
                      <div className="flex items-start gap-1 pt-1">
                        <ExternalLink className="h-3 w-3 mt-0.5 shrink-0" />
                        <a
                          href={m.telegram_invite_url}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="text-foreground/80 break-all underline-offset-2 hover:underline"
                        >
                          {m.telegram_invite_url}
                        </a>
                      </div>
                    )}
                  </div>
                </div>

                <div className="space-y-3 border-t border-border/60 pt-4">
                  <div className="space-y-1.5">
                    <Label
                      htmlFor={`access-${m.id}`}
                      className="text-xs text-muted-foreground flex items-center gap-1.5"
                    >
                      <ExternalLink className="w-3.5 h-3.5" />
                      URL de acceso free (destino del email)
                    </Label>
                    <Input
                      id={`access-${m.id}`}
                      value={state.accessUrl}
                      onChange={(e) =>
                        setRowState((prev) => ({
                          ...prev,
                          [m.id]: { ...state, accessUrl: e.target.value },
                        }))
                      }
                      placeholder="https://t.me/+…  o  https://… (otra URL free)"
                      className="h-10 border-border bg-muted/50 font-mono text-sm text-foreground placeholder:text-muted-foreground"
                    />
                    <p className="text-[11px] text-muted-foreground">
                      Free / privado. No pegues checkout ni /invitacion-exclusive.
                    </p>
                  </div>

                  <div className="space-y-1.5">
                    <Label htmlFor={`msg-${m.id}`} className="text-xs text-muted-foreground">
                      Mensaje al email del comprador (opcional)
                    </Label>
                    <Textarea
                      id={`msg-${m.id}`}
                      value={state.personalMessage}
                      onChange={(e) =>
                        setRowState((prev) => ({
                          ...prev,
                          [m.id]: { ...state, personalMessage: e.target.value },
                        }))
                      }
                      placeholder="Ej: Acá está tu acceso al grupo — abrí el botón cuando quieras."
                      rows={2}
                      maxLength={800}
                      className="border-border bg-muted/50 text-sm text-foreground placeholder:text-muted-foreground"
                    />
                  </div>

                  <div className="space-y-1.5">
                    <Label htmlFor={`notes-${m.id}`} className="text-xs text-muted-foreground">
                      Notas internas (no van al email)
                    </Label>
                    <Textarea
                      id={`notes-${m.id}`}
                      value={state.notes}
                      onChange={(e) =>
                        setRowState((prev) => ({
                          ...prev,
                          [m.id]: { ...state, notes: e.target.value },
                        }))
                      }
                      placeholder="Solo para el equipo"
                      rows={1}
                      className="border-border bg-muted/50 text-sm text-foreground placeholder:text-muted-foreground"
                    />
                  </div>

                  <div className="flex justify-end">
                    <Button
                      onClick={() => void approve(m)}
                      disabled={state.saving || !canSubmit}
                      className={
                        isApproved
                          ? "gap-2 bg-secondary text-secondary-foreground hover:bg-secondary/90"
                          : "bg-emerald-500 hover:bg-emerald-400 text-emerald-950 font-semibold gap-2"
                      }
                    >
                      {state.saving ? (
                        <>
                          <Loader2 className="w-4 h-4 animate-spin" />
                          Enviando…
                        </>
                      ) : isApproved ? (
                        <>
                          <Save className="w-4 h-4" />
                          Actualizar y reenviar email
                        </>
                      ) : (
                        <>
                          <Sparkles className="w-4 h-4" />
                          Aprobar y enviar email
                        </>
                      )}
                    </Button>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
